import React, { useState } from 'react';

interface ValidationIndicatorProps {
  fieldName: string;
  contextRef: string;
}

/**
 * ValidationIndicator - Pre-rendered error indicator with hover tooltip
 * React owns this node, validation hook just toggles visibility
 * 
 * CRITICAL: Must be pre-rendered so React's fiber tree knows it exists!
 * Validation hook will find this and toggle display + set data-error-message attribute
 * 
 * ✅ SAFE: Tooltip div is ALWAYS rendered (no conditional rendering)
 * Just hidden with CSS to prevent React reconciliation conflicts
 */
export const ValidationIndicator: React.FC<ValidationIndicatorProps> = ({ fieldName, contextRef }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipMessage, setTooltipMessage] = useState('');
  
  const handleMouseEnter = (e: React.MouseEvent<HTMLSpanElement>) => {
    // Read message from data attribute (set by validation hook)
    const message = e.currentTarget.getAttribute('data-error-message') || 'Validation error';
    setTooltipMessage(message);
    setShowTooltip(true);
  };
  
  const handleMouseLeave = () => {
    setShowTooltip(false);
  };
  
  return (
    <span 
      className="validation-error-indicator"
      data-field-name={fieldName}
      data-context-ref={contextRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        position: 'absolute',
        right: '8px',
        top: '50%',
        transform: 'translateY(-50%)',
        display: 'none',
        alignItems: 'center',
        justifyContent: 'center',
        width: '16px',
        height: '16px',
        background: '#dc3545',
        color: 'white',
        borderRadius: '50%',
        cursor: 'help',
        fontSize: '11px',
        fontWeight: 'bold',
        zIndex: 10
      }}
    >
      !
      {/* ✅ ALWAYS render tooltip (no conditional) - just hide with CSS */}
      <div 
        className="validation-error-tooltip-hover"
        style={{
          position: 'absolute',
          bottom: 'calc(100% + 8px)',
          right: 0,
          background: '#fff',
          color: '#333',
          padding: '12px 16px',
          borderRadius: '8px',
          fontSize: '13px',
          lineHeight: 1.4,
          width: 'max-content',
          maxWidth: '400px',
          minWidth: '120px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          border: '1px solid #e1e5e9',
          whiteSpace: 'normal',
          wordWrap: 'break-word',
          zIndex: 10000,
          pointerEvents: 'none',
          display: showTooltip && tooltipMessage ? 'block' : 'none' // ✅ CSS toggle, not conditional render
        }}
      >
        <div style={{ fontWeight: 600, color: '#dc3545' }}>
          {tooltipMessage || 'Validation error'}
        </div>
      </div>
    </span>
  );
};